import { useState, useEffect, useCallback, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getVirtualChildLocations } from "../utils"
import { SensorType } from "@/api/SensorsService"
import { AlertModalData } from "@/components/shared/Header"
import useDialog from "@/hooks/useDialog"
import { useFastClearAlert } from "@/hooks/useFastClearAlert"
import {
   useRealtimeConnection,
   RealtimeProducer,
} from "@/hooks/useRealtimeConnection"
import { AppDispatch, RootState } from "@/store"
import {
   seedCellsFromLevels,
   resetCells,
   updateLevelByCell,
   cellsSelectors,
} from "@/store/cellsSlice"
import { fetchFacility, selectDeepestLevelId } from "@/store/facilitiesSlice"
import { fetchInmatesByLevel } from "@/store/inmatesByLevelSlice"
import { fetchSensors } from "@/store/sensorsSlice"
import {
   resetUnknownLocationWearables,
   selectUnknownLocationProducers,
} from "@/store/unknownLocationWearablesSlice"
import { fetchRtsToken } from "@/store/userSlice"
import { isConditionError } from "@/store/utils"
import { resetWearables, switchLevel } from "@/store/wearablesRealtimeSlice"
import { Alert } from "@/types/alert"
import { Token } from "@/types/index"
import { prepareAlertModalData } from "@/utils/alerts"
import { sortByField } from "@/utils/index"

export const useFloorOverviewData = (producers: RealtimeProducer[]) => {
   const dispatch = useDispatch<AppDispatch>()
   const { loading, error } = useSelector(
      ({ cellsReducer }: RootState) => cellsReducer
   )

   const { facilityDetails } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )

   const { isAuthenticated } = useSelector(
      ({ authReducer }: RootState) => authReducer
   )

   const { rtsToken, rtsTokenError } = useSelector(
      ({ userReducer }: RootState) => userReducer
   )

   const parentLevelId = useSelector(selectDeepestLevelId)
   const { loading: inmatesSeedLoading, currentLevelId: inmatesSeedLevelId } =
      useSelector(
         ({ inmatesByLevelReducer }: RootState) => inmatesByLevelReducer
      )

   const cells = useSelector(cellsSelectors.selectAll)
   const sortedCells = sortByField(cells, "name")
   const [occupiedCells, setOccupiedCells] = useState({
      occupiedCells: 0,
      totalCells: 0,
   })
   const [alertModalData, setAlertModalData] = useState({} as AlertModalData)
   const { handleClearAlert } = useFastClearAlert()
   const { isOpen: isDialogOpen, openDialog, closeDialog } = useDialog(false)

   const unknownLocationProducers = useSelector(selectUnknownLocationProducers)
   const combinedProducers = useMemo(
      () => [...producers, ...unknownLocationProducers],
      [producers, unknownLocationProducers]
   )

   const isFacilityReady = !!parentLevelId && !!facilityDetails?.levels?.length
   const isLevelSeedSettled =
      isFacilityReady &&
      inmatesSeedLevelId === parentLevelId &&
      !inmatesSeedLoading

   const [hasLevelSeedEverSettled, setHasLevelSeedEverSettled] = useState(false)
   useEffect(() => {
      if (isLevelSeedSettled) setHasLevelSeedEverSettled(true)
   }, [isLevelSeedSettled])

   const [settledProducers, setSettledProducers] = useState<RealtimeProducer[]>(
      []
   )
   useEffect(() => {
      if (isLevelSeedSettled) setSettledProducers(combinedProducers)
   }, [isLevelSeedSettled, combinedProducers])

   useRealtimeConnection({
      rtsToken: hasLevelSeedEverSettled ? rtsToken : null,
      producers: settledProducers,
   })

   useEffect(() => {
      if (!isAuthenticated) return

      async function fetchLevelsData() {
         const facilityId = sessionStorage.getItem(Token.FACILITY_ID)
         if (!facilityId) return

         try {
            await dispatch(fetchRtsToken()).unwrap()
         } catch (error) {
            console.error("Failed to fetch RTS token:", error)
         }

         try {
            await dispatch(fetchFacility()).unwrap()
         } catch (error) {
            if (!isConditionError(error))
               console.error("Facility levels fetch failed:", error)
         }
      }

      fetchLevelsData()

      return () => {
         dispatch(resetCells())
         dispatch(resetWearables())
         dispatch(resetUnknownLocationWearables())
      }
   }, [dispatch, isAuthenticated])

   useEffect(() => {
      dispatch(fetchSensors({ type: SensorType.WEARABLE }))
   }, [dispatch])

   useEffect(() => {
      if (!parentLevelId || !facilityDetails?.levels?.length) return
      dispatch(switchLevel(parentLevelId))
      dispatch(
         seedCellsFromLevels({
            levels: facilityDetails.levels,
            parentLevelId,
         })
      )
   }, [dispatch, parentLevelId, facilityDetails])

   useEffect(() => {
      if (!parentLevelId || !facilityDetails?.levels?.length) return
      const locations = getVirtualChildLocations(
         facilityDetails.levels,
         parentLevelId
      )
      dispatch(fetchInmatesByLevel({ levelId: parentLevelId, locations }))
      // Deliberately guarded to parentLevelId only, not facilityDetails: this
      // fetch should re-run when the selected level changes, not whenever
      // facilityDetails happens to get a new reference for an unrelated
      // reason.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dispatch, parentLevelId])

   // useEffect(() => {
   //    if (!loading && cells) {
   //       setOccupiedCells(getOccupiedCellsCount(cells))
   //    }
   // }, [cells, loading])

   const changeCellStatus = useCallback(
      async (
         cellId: string,
         status: boolean,
         isMaintenanceField: boolean = false
      ) => {
         const field = isMaintenanceField ? "maintenance" : "vacant"
         try {
            await dispatch(
               updateLevelByCell({
                  levelId: cellId,
                  levelForm: { [field]: status },
               })
            ).unwrap()
         } catch (error) {
            console.error("Error setting status:", error)
         }
      },
      [dispatch]
   )

   const handleClearAlertClick = useCallback(
      (alert: Alert) => {
         setAlertModalData(prepareAlertModalData(alert))
         openDialog()
      },
      [openDialog]
   )

   const handleClearAlertAction = useCallback(
      () => handleClearAlert(alertModalData.id, closeDialog),
      [alertModalData.id, closeDialog, handleClearAlert]
   )

   return {
      loading,
      error: error ?? rtsTokenError,
      cells: sortedCells,
      occupiedCells,
      changeCellStatus,
      handleClearAlert: handleClearAlertAction,
      handleClearAlertClick,
      isDialogOpen,
      closeDialog,
   }
}
